import type { ActivityEntryDto } from "@gladis/shared";
import { formatDateTime, useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import { destinationFor, type ActivityDestination } from "../../catalog/destinations.js";

/**
 * One row of the feed: when, who, what kind, and what happened.
 *
 * ==========================================================================================
 * THE ACTOR'S NAME APPEARS TWICE, ON PURPOSE
 * ==========================================================================================
 *
 * FR-AL4 requires the actor as a field, and every writer already opens its `message` with the actor's
 * name — "Damien updated the balance of Emergency Fund". So the row shows both, and the message is NOT
 * rewritten or trimmed to remove it.
 *
 * Parsing a name back out of a sentence composed by six different contexts is a worse failure than
 * repeating it: it would work until someone was called "Updated", and the fix would be a regular
 * expression owned by nobody. The duplication is visible and harmless; the alternative is invisible and
 * not.
 *
 * ==========================================================================================
 * AN ACTIVATING CONTROL, NOT AN ANCHOR
 * ==========================================================================================
 *
 * The row renders as a `button` when it has a destination and as a plain `div` when it does not. No domain
 * UI package in this repository imports a router or renders an `href` — navigation is `apps/app`'s job,
 * reached through `onOpen`, exactly as `InvestmentsPage` reaches it through `onOpenStock`. AC-AL9's
 * original wording said "rendered as a link", and `activity-log.md` was amended to say what this
 * application actually has.
 *
 * A row with no destination is NOT a disabled button. It is not a control at all — there is nothing to
 * activate, and a disabled button announces an action that has been taken away rather than one that never
 * existed.
 */
export interface ActivityRowProps {
  entry: ActivityEntryDto;
  onOpen: (destination: ActivityDestination) => void;
}

export function ActivityRow({ entry, onOpen }: ActivityRowProps): ReactNode {
  const t = useT();
  const destination = destinationFor(entry.entityType, entry.entityId);
  const isFailure = entry.status === "error";

  const content = (
    <>
      <time
        dateTime={entry.occurredAt}
        className="w-36 shrink-0 text-xs tabular-nums text-muted"
        // The full timestamp on hover. The row shows a readable one; the exact instant is what someone
        // reconstructing a sequence of events needs, and it is the one thing the feed knows precisely.
        title={entry.occurredAt}
      >
        {formatWhen(entry.occurredAt)}
      </time>

      <span className="w-28 shrink-0 truncate text-sm text-ink" title={entry.actorName}>
        {entry.isSystem ? "System" : entry.actorName}
      </span>

      <span
        className={
          isFailure
            ? "shrink-0 rounded-control border border-danger/40 bg-danger/10 px-2 py-0.5 text-xs text-danger"
            : "shrink-0 rounded-control border border-input bg-input/30 px-2 py-0.5 text-xs text-muted"
        }
      >
        {t(`activity.event.${entry.eventType}`)}
      </span>

      <span className="min-w-0 flex-1 text-sm text-ink">{entry.message}</span>
    </>
  );

  const shared = "flex w-full items-center gap-3 border-b border-line px-2 py-3 text-left";

  if (destination.kind === "none") {
    return <div className={shared}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => {
        onOpen(destination);
      }}
      className={`${shared} cursor-pointer transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none`}
    >
      {content}
    </button>
  );
}

/**
 * The timestamp, readable.
 *
 * No date grouping and no relative "2 hours ago" — both are Non-Goals in `activity-log.md`, and relative
 * time in an audit trail is the wrong unit: the question the log answers is "when did this happen", not
 * "how long ago".
 *
 * IT FOLLOWS THE READER'S LANGUAGE, NOT THE BROWSER'S OWN LOCALE (021). This used to pass
 * `undefined` to `toLocaleString`, which reads whatever the browser reports — so a member who chose
 * Dutch on an English machine got Dutch words above an English date. `formatDateTime` resolves from
 * the language in force, which is the one place every displayed date now agrees on.
 *
 * THE ENTRY'S OWN SENTENCE IS NOT TOUCHED. `message` is a stored English sentence and renders
 * exactly as written — the page around the feed is translated and the feed's contents are not,
 * which is FR-027 and is what keeps every already-written row readable.
 */
function formatWhen(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  return formatDateTime(at);
}
